"use client";

import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
// import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, Search } from "lucide-react";
import StudentDetails from "./student-details";
import studentAPIService from "@/lib/student-api.service";
import { Student } from "@/lib/student.schema";
import { ErrorMessages } from "@/lib/utils";

interface SearchState {
	student: Student | null;
	error: string | null;
	success: boolean;
	loading: boolean;
}

/**
 * Khoá tuyển sinh mà bàn tiếp nhận đang làm việc, theo hai chữ số đầu của MSSV.
 *
 * Sang đợt tuyển sinh sau thì đổi đúng hằng số này. Cố ý KHÔNG suy ra từ năm hệ
 * thống: đợt tiếp nhận vắt qua giao thừa, và một cái `new Date().getFullYear()`
 * sẽ tự khoá cả bàn tiếp nhận vào đúng ngày 1/1 mà không ai đụng vào code.
 */
const CURRENT_COHORT_PREFIX = "26";

interface StudentSearchProps {
	/** MSSV lấy từ `/dashboard/:mssv` — tra ngay khi mở trang. */
	initialMssv?: string;
}

export default function StudentSearch({ initialMssv }: StudentSearchProps) {
	const [state, setState] = useState<SearchState>({
		student: null,
		error: null,
		success: false,
		loading: false,
	});
	const [studentId, setStudentId] = useState("");
	const [autoScreenshot, _] = useState<boolean>(() => {
		const saved = localStorage.getItem("autoScreenshot");
		return saved === null ? false : saved === "true";
	});
	const studentDetailsRef = useRef<HTMLDivElement>(null);
	const navigate = useNavigate();
	/** MSSV vừa tra, để phân biệt URL do chính mình đổi với URL người dùng mở. */
	const lastSearched = useRef<string | null>(null);

	// Auto-scroll when student data appears
	useEffect(() => {
		if (state.student && studentDetailsRef.current) {
			setTimeout(() => {
				studentDetailsRef.current?.scrollIntoView({
					behavior: "smooth",
					block: "start",
				});
			}, 100);
		}
	}, [state.student]);

	// Save autoScreenshot setting to localStorage when changed
	useEffect(() => {
		//localStorage.setItem("autoScreenshot", autoScreenshot.toString());
	}, [autoScreenshot]);

	/**
	 * Gỡ MSSV khỏi URL sau một lần tra hỏng.
	 *
	 * Thẻ sinh viên tự biến mất khi `state.student` về `null`, nhưng URL thì không:
	 * nó vẫn là `/dashboard/<mssv cũ>`. Dashboard vì thế vẫn GHIM hồ sơ trước đó —
	 * reload, bấm Back, hay mở lại chính đường link ấy là hồ sơ cũ hiện lên nguyên
	 * vẹn, kèm ô tìm kiếm tự điền lại MSSV cũ, dù lần tra gần nhất không ra gì.
	 *
	 * Không gây vòng lặp: `initialMssv` thành rỗng, mà effect ở dưới thoát ngay khi
	 * không có `initialMssv`.
	 */
	const clearPinnedMssv = () => navigate("/dashboard", { replace: true });

	/**
	 * Sửa ô tìm kiếm thì bỏ luôn kết quả của lần tra trước.
	 *
	 * Nút Tìm kiếm chết khi MSSV chưa đủ 8 ký tự, nên gõ một MSSV ngắn hơn KHÔNG
	 * chạy lần tra nào: không có API call, không có nhánh lỗi, không có gì dọn
	 * màn hình. Thẻ của hồ sơ trước nằm lại nguyên vẹn bên dưới một ô tìm kiếm
	 * đang ghi thứ khác — người dùng đọc nó như câu trả lời cho MSSV vừa gõ.
	 * Ghi nhận thực tế: tra `26125021` xong gõ `2612` thì màn hình vẫn là hồ sơ
	 * của `26125021`.
	 *
	 * Nên thẻ chỉ được phép tồn tại khi nó đúng là hồ sơ của MSSV đang nằm trong
	 * ô. Khác một ký tự cũng là câu hỏi khác, và câu trả lời cũ không còn đúng.
	 */
	const handleQueryChange = (value: string) => {
		setStudentId(value);

		if (!state.student || value === state.student.mssv) return;

		setState((prev) => ({ ...prev, student: null, success: false, error: null }));
		clearPinnedMssv();
	};

	/**
	 * Tra một MSSV.
	 *
	 * Tách khỏi handler của form vì có hai đường vào — người dùng bấm Tìm kiếm, và
	 * `/dashboard/:mssv` mở sẵn một hồ sơ — và cả hai phải cho cùng kết quả lẫn
	 * cùng thông báo lỗi.
	 */
	const runSearch = async (raw: string) => {
		const mssv = raw.trim();
		// Đánh dấu trước khi gọi API: URL được cập nhật sau mỗi lần tìm thành công,
		// mà đổi URL lại chạy effect ở dưới. Không có mốc này thì mỗi lần tìm sẽ
		// thành hai lần gọi API.
		lastSearched.current = mssv;

		if (mssv.length !== 8) {
			setState((prev) => ({
				...prev,
				error: "MSSV phải có đúng 8 ký tự",
				success: false,
				student: null,
			}));
			clearPinnedMssv();
			return;
		}

		// Chặn ở đây chứ không ở handler của form, vì `runSearch` có hai đường vào và
		// đường kia là `/dashboard/:mssv` — dán một link khoá cũ phải bị từ chối y
		// như gõ tay, nếu không thì luật này chỉ là một gợi ý.
		//
		// Chặn phía giao diện, KHÔNG phải phía quyền: API vẫn trả hồ sơ khoá cũ cho
		// ai gọi thẳng. Đây là để bàn tiếp nhận khỏi mở nhầm hồ sơ năm ngoái, không
		// phải để giấu dữ liệu.
		if (!mssv.startsWith(CURRENT_COHORT_PREFIX)) {
			setState((prev) => ({
				...prev,
				error: `Chỉ tra cứu được sinh viên khoá ${CURRENT_COHORT_PREFIX} (MSSV bắt đầu bằng ${CURRENT_COHORT_PREFIX}).`,
				success: false,
				student: null,
			}));
			clearPinnedMssv();
			return;
		}

		setState((prev) => ({ ...prev, loading: true, error: null }));

		try {
			const student = await studentAPIService.getStudentByMSSV(mssv);
			setState({
				student,
				error: null,
				success: true,
				loading: false,
			});
			// Để đường dẫn luôn chia sẻ được: mở lại link này là ra đúng hồ sơ đó.
			// `replace` để nút Back không phải lùi qua từng lần tra.
			navigate(`/dashboard/${mssv}`, { replace: true });
		} catch (error) {
			// Không đẩy message thô của API ra màn hình (quy tắc 2 trong CLAUDE.md).
			//
			// Phân loại bằng HTTP status, không phải bằng câu chữ. Bản trước đọc
			// message bằng regex `/404|not found/i`, nên nó phụ thuộc vào NGÔN NGỮ của
			// một câu do backend viết — đúng thứ vừa được dịch sang tiếng Việt. Một
			// câu chữ là bản dịch, một mã status là hợp đồng.
			//
			// Vẫn giữ nhánh đọc chữ làm dự phòng: lỗi ném ra từ nơi khác
			// (`fetch` hỏng, schema Zod không khớp) không mang `status`.
			const status = (error as { status?: number } | null)?.status;
			const raw = error instanceof Error ? error.message : "";
			const notFound = status === 404 || /404|not found|không tìm thấy/i.test(raw);

			setState({
				student: null,
				error: notFound
					? "Không tìm thấy sinh viên với MSSV này"
					: `${ErrorMessages.SERVER_ERROR} Vui lòng thử lại hoặc kiểm tra kết nối.`,
				success: false,
				loading: false,
			});
			// Gỡ cả với lỗi máy chủ, không riêng "không tìm thấy": màn hình đang báo
			// lỗi thì URL không được nói rằng nó đang hiển thị một hồ sơ.
			clearPinnedMssv();
		}
	};

	const handleSearch = (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		runSearch(studentId);
	};

	// Mở `/dashboard/:mssv` thì tra luôn, không bắt người dùng gõ lại. Chỉ chạy khi
	// MSSV trên URL khác lần tra gần nhất, nên việc `runSearch` tự đổi URL sau khi
	// thành công không kích hoạt vòng lặp.
	useEffect(() => {
		if (!initialMssv || initialMssv === lastSearched.current) return;
		setStudentId(initialMssv.slice(0, 8));
		runSearch(initialMssv);
		// Deps chỉ có `initialMssv`: `runSearch` được tạo mới mỗi lần render, đưa nó
		// vào đây thì effect chạy lại sau mỗi `setState` bên trong chính nó.
	}, [initialMssv]);

	return (
		<div className="space-y-6">
			{/* The search bar is a screen control — it has no place on the printed sheet. */}
			<Card className="print:hidden">
				<CardContent>
					{/* Xếp dọc dưới sm: ô nhập và nút cùng một hàng trên máy hẹp thì nút bị
					    bóp còn vài chục pixel, chữ "Đang tìm kiếm..." không còn chỗ. */}
					<form onSubmit={handleSearch} className="flex flex-col gap-3 sm:flex-row sm:gap-4">
						<div className="flex-1">
							<Input
								name="studentId"
								value={studentId}
								onChange={(e) => handleQueryChange(e.target.value.trim().slice(0, 8))}
								placeholder="Nhập MSSV (ví dụ: 25180140)"
								className="w-full"
								required
								disabled={state.loading}
							/>
						</div>
						<Button
							type="submit"
							disabled={state.loading || studentId.length !== 8}
							className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-white"
						>
							{state.loading ? (
								<>
									<Loader2 className="mr-2 h-4 w-4 animate-spin" />
									Đang tìm kiếm...
								</>
							) : (
								<>
									<Search className="mr-2 h-4 w-4" />
									Tìm kiếm
								</>
							)}
						</Button>
						{/* <div className="flex items-center space-x-2">
							<Checkbox
								id="auto-screenshot"
								checked={autoScreenshot}
								onCheckedChange={(checked) => setAutoScreenshot(checked === true)}
							/>
							<label
								htmlFor="auto-screenshot"
								className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer flex items-center gap-1"
							>
								<Camera className="h-4 w-4" />
								Tự động lưu ảnh
							</label>
						</div> */}
					</form>

					{state.error && (
						<div className="mt-4 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded">
							{state.error}
						</div>
					)}

					{/* Nút Tìm kiếm chết cho tới khi đủ 8 ký tự. Không nói ra thì người
					    dùng gõ một MSSV ngắn, bấm mãi không được, và không biết vì sao —
					    nhất là khi thẻ hồ sơ cũ vừa biến mất khỏi màn hình. Màu xám chứ
					    không phải đỏ: đây là trạng thái đang gõ dở, chưa phải lỗi. */}
					{studentId.length > 0 && studentId.length < 8 && !state.error && (
						<p className="mt-2 text-sm text-muted-foreground">
							MSSV phải đủ 8 chữ số — đang có {studentId.length}.
						</p>
					)}
				</CardContent>
			</Card>

			{state.success && state.student && (
				<div ref={studentDetailsRef}>
					<StudentDetails student={state.student} autoScreenshot={autoScreenshot} />
				</div>
			)}
		</div>
	);
}
